import { Module, MutationTree, GetterTree, ActionTree } from "vuex"
import { RootState } from ".."
import { Route } from "vue-router"
import { BreadcrumbItem, CategorizedNodes, Company, MenuPayload, Node } from "@/types/breadcrumbs"

export interface BreadcrumbsState {
    items: BreadcrumbItem[]
    categorizedData: CategorizedNodes
    entityNames: {
        [entityType: string]: {
            [id: string]: string
        }
    }
    isInitialized: boolean
    isLoading: boolean
    currentPath: string
}

const state: BreadcrumbsState = {
    items: [],
    categorizedData: {},
    entityNames: {},
    isInitialized: false,
    isLoading: false,
    currentPath: "",
}

const mutations: MutationTree<BreadcrumbsState> = {
    setItems(state, items: BreadcrumbItem[]) {
        state.items = items
    },
    setCategorizedData(state, categorizedData: CategorizedNodes) {
        state.categorizedData = categorizedData
    },
    setEntityName(state, { id, entityType, name }) {
        if (!state.entityNames[entityType]) {
            state.entityNames[entityType] = {}
        }

        state.entityNames[entityType][id] = name
    },
    setEntityNames(state, { entityType, names }) {
        if (!state.entityNames[entityType]) {
            state.entityNames[entityType] = {}
        }

        state.entityNames[entityType] = {
            ...state.entityNames[entityType],
            ...names,
        }
    },
    clearEntityNames(state) {
        state.entityNames = {}
    },
    setInitialized(state, value: boolean) {
        state.isInitialized = value
    },
    setLoading(state, value: boolean) {
        state.isLoading = value
    },
    setCurrentPath(state, path: string) {
        state.currentPath = path
    },
}

const getters: GetterTree<BreadcrumbsState, RootState> = {
    items: (state) => state.items,
    getEntityName: (state) => (id: string, entityType: string) => {
        return {
            id,
            name: state.entityNames[entityType]?.[id] || id,
        }
    },
    getEntityNames: (state) => (entityType: string) => {
        return state.entityNames[entityType] || {}
    },
    getEntityNamesByType: (state) => (entityType: string) => {
        return state.entityNames[entityType] || {}
    },
    getEntityNamesById: (state) => (entityType: string, ids: string[]) => {
        return ids.map((id) => state.entityNames[entityType]?.[id] || id)
    },
    getCategorizedData: (state) => (type: string, id: string) => {
        return state.categorizedData[type]?.[id]
    },
    isInitialized: (state) => state.isInitialized,
    isLoading: (state) => state.isLoading,
    currentPath: (state) => state.currentPath,
}

const actions: ActionTree<BreadcrumbsState, RootState> = {
    async updateBreadcrumbs({ commit, rootGetters, getters, state }, route: Route) {
        commit("setLoading", true)
        try {
            commit("setCurrentPath", route.path)

            const pathSegments = route.path.split("/").filter((segment) => segment)
            let keyCounter = 0
            const generateKey = () => keyCounter++

            const items: BreadcrumbItem[] = []

            if (rootGetters["navigationContext/hasContext"]) {
                const context = rootGetters["navigationContext/getContext"]
                items.push({
                    id: generateKey(),
                    text: context.sourceTitle,
                    path: context.returnPath,
                    doNotVisit: false,
                    isContextual: true,
                    type: "context",
                })
            }

            const regularBreadcrumbs = pathSegments
                .map((segment, index) => {
                    const currentPath = "/" + pathSegments.slice(0, index + 1).join("/")

                    const matchedRoute = route.matched.find((r) => {
                        const routePattern = r.path
                            .replace(/:[^/]+/g, "[^/]+")
                            .replace(/\//g, "\\/")
                        const pattern = new RegExp(`^${routePattern}$`)
                        return pattern.test(currentPath)
                    })

                    if (!matchedRoute) {
                        return null
                    }

                    const meta = matchedRoute.meta || {}
                    if (meta.doNotShow) {
                        return null
                    }

                    if (meta.useDynamicTitle) {
                        return {
                            id: generateKey(),
                            text: meta.title,
                            path: currentPath,
                            type: "dynamic",
                        }
                    }

                    let text = meta?.title || segment
                    let path = currentPath

                    if (matchedRoute.path.includes(":") && !isNaN(Number(segment))) {
                        const entityType = meta.type
                        const useTitle = meta.useTitle

                        if (useTitle) {
                            return {
                                id: generateKey(),
                                text,
                                path,
                                type: entityType,
                            }
                        }

                        if (entityType) {
                            if (!state.isInitialized) {
                                return null
                            }

                            const entityData = getters.getCategorizedData(entityType, segment)

                            if (!entityData?.title) {
                                return null
                            }

                            if (entityType === "company") {
                                const nextSegmentIndex = index + 2

                                if (nextSegmentIndex < pathSegments.length) {
                                    const locationGroupId = pathSegments[nextSegmentIndex]
                                    const locationGroupData = getters.getCategorizedData(
                                        "location-group",
                                        locationGroupId,
                                    )

                                    if (locationGroupData?.title === entityData.title) {
                                        return null
                                    }
                                }

                                const companyData = getters.getCategorizedData("company", segment)

                                path = companyData?.href || path
                            }

                            text = entityData.title

                            return {
                                id: generateKey(),
                                text,
                                path,
                                doNotVisit: meta?.doNotVisit || false,
                                type: entityType,
                                data: entityData.data,
                            }
                        } else {
                            return null
                        }
                    }

                    return {
                        id: generateKey(),
                        text,
                        path,
                        doNotVisit: meta?.doNotVisit || false,
                        type: meta.type || matchedRoute.name || "default",
                    }
                })
                .filter((item): item is BreadcrumbItem => item !== null)

            items.push(...regularBreadcrumbs)
            commit("setItems", items)
        } finally {
            commit("setLoading", false)
        }
    },
    createFlatBreadcrumbData({ commit }, { menu }: MenuPayload) {
        const categorizedData: CategorizedNodes = {}

        const processNode = (nodes: Node[]) => {
            nodes.forEach((node) => {
                const nodeType = node.type?.toLowerCase() || "other"

                if (!categorizedData[nodeType]) {
                    categorizedData[nodeType] = {}
                }

                const id = node.typeId || node.href?.split("/")[2] || "unknown"
                categorizedData[nodeType][id] = node

                if (nodeType === "company" && node.href) {
                    const match = node.href.match(/\/companies\/(\d+)\/location-groups\/(\d+)/)
                    if (match) {
                        const [, companyId, locationGroupId] = match

                        if (!categorizedData["location-group"]) {
                            categorizedData["location-group"] = {}
                        }

                        categorizedData["location-group"][locationGroupId] = {
                            id: locationGroupId,
                            level: node.level + 1,
                            data: {
                                company: { id: parseInt(companyId), name: node.title },
                                locationGroup: {
                                    id: parseInt(locationGroupId),
                                    name: node.title,
                                },
                            },
                            title: node.title,
                            type: "location-group",
                            typeId: locationGroupId,
                            href: node.href,
                            aliases: [],
                            children: [],
                        }
                    }
                }

                if (node.children) {
                    processNode(node.children)
                }
            })
        }

        processNode(menu)

        commit("setCategorizedData", categorizedData)
        commit("setInitialized", true)
    },
    transformCompaniesData({ commit, state }, companies: Company[]): void {
        const newCategorizedData = {
            ...state.categorizedData,
            company: {
                ...(state.categorizedData.company || {}),
            },
        }

        companies.forEach((company) => {
            newCategorizedData.company[company.id] = {
                id: company.id.toString(),
                level: 0,
                data: { company },
                title: company.name,
                type: "company",
                typeId: company.id.toString(),
                href: `/settings/global/companies/${company.id}`,
                aliases: [],
                children: [],
            }
        })

        commit("setCategorizedData", newCategorizedData)
        commit("setInitialized", true)
    },
}

const breadcrumbsModule: Module<BreadcrumbsState, RootState> = {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
}

export default breadcrumbsModule
